import { useContext, useState, useEffect, useRef } from "react";
import { useFormik } from "formik";

// react-router-dom components
import { useNavigate } from "react-router-dom";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import * as Yup from "yup";
// Authentication layout components
import AuthLayout from "layouts/authentication/components/AuthLayout";

// Image
import ccLogo from "assets/cc-images/form-side-img.svg";
import { CircularProgress, Button, Typography } from "@mui/material"; import CustomOTPInput from "components/OTPInput";
import MDSnackbar from "components/MDSnackbar";
import { LoginFormContext } from "context/loginFormCtx";
import { superAdminOtpVerificationApi, resendSuperAdminOtpApi, requestTotpResetApi } from "lib/Api/Authentication";
import { VALIDATION_MESSAGES } from "utils/constants";
import { CommonContext } from "context/CommonCtx";
import DraggableDialog from "components/draggagle-dialog";
import { KeyboardArrowLeft } from "@mui/icons-material";

function SuperAdminOtpVerification() {
  const { setNotification, notification, setUserDetail, LogIn } = useContext(LoginFormContext);
  const { CCLoader } = useContext(CommonContext);

  const navigate = useNavigate();

  const [otp, setOtp] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [countdown, setCountdown] = useState(30);
  const [isVerifying, setIsVerifying] = useState(false);
  const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false);

  const timerRef = useRef(null);

  // Get user data from session storage (set during login)
  const userData = JSON.parse(sessionStorage.getItem("superadmin_login_data") || "{}");

  // Determine MFA method based on user data
  const getMfaMethod = () => {
    if (userData?.userData?.mfa_method === 2) {
      return "totp";
    } else if (userData?.userData?.mfa_method === 1) {
      return "email";
    }
    return "email"; // Default to email if not specified
  };

  const handleConfirmSwitchToEmail = async () => {
    if (!userData.email) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "Session expired. Please login again.",
        open: true,
      });
      navigate("/login", { replace: true });
      return;
    }

    try {
      CCLoader(true);
      const response = await resendSuperAdminOtpApi({
        email: userData.email,
        switch_to_email: true,
        mfa_method: userData?.userData?.mfa_method,
      });

      if (response?.status === 200) {
        // Update local/session MFA method to email
        try {
          const updated = { ...userData, userData: { ...userData.userData, mfa_method: 1 } };
          sessionStorage.setItem("superadmin_login_data", JSON.stringify(updated));
        } catch (e) {
          // ignore session storage failure
        }
        setMfaMethod("email");
        setNotification({
          color: "success",
          icon: "check",
          title: "Switched to Email OTP",
          content: "We've sent a new OTP to your email and switched your 2FA method.",
          open: true,
        });
        setConfirmSwitchOpen(false);
        startCountdown();
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response?.data?.message || "Failed to switch to Email OTP. Please try again.",
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "An error occurred while switching to Email OTP. Please try again.",
        open: true,
      });
    } finally {
      CCLoader(false);
    }
  };

  const [mfaMethod, setMfaMethod] = useState(getMfaMethod());

  const startCountdown = () => {
    clearInterval(timerRef.current);
    setCountdown(30);
    setResendDisabled(true);

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setResendDisabled(false);
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startCountdown();
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values) => {
    if (!userData.email) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "Session expired. Please login again.",
        open: true,
      });
      // navigate("/login", { replace: true });
      return;
    }

    setIsVerifying(true);
    try {
      const response = await superAdminOtpVerificationApi({
        email: userData.email,
        otp: values.otp,
      });

      if (response?.status === 200) {
        // Set user data and login
        setUserDetail(response.data.data);
        LogIn(response.data.data);

        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content:
            mfaMethod === "totp"
              ? "TOTP verified successfully. Welcome to CasePath!"
              : "Email OTP verified successfully. Welcome to CasePath!",
          open: true,
        });
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content:
            response.data?.message ||
            (mfaMethod === "totp"
              ? "Invalid TOTP code. Please try again."
              : "Invalid OTP code. Please try again."),
          open: true,
        });
        setIsVerifying(false);
        CCLoader(false);
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content:
          mfaMethod === "totp"
            ? "An error occurred during TOTP verification. Please try again."
            : "An error occurred during email OTP verification. Please try again.",
        open: true,
      });
      setIsVerifying(false);
      CCLoader(false);
    } finally {
      setIsVerifying(false);
      CCLoader(false);
    }
  };

  const handleResendOtp = async () => {
    if (!userData.email) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "Session expired. Please login again.",
        open: true,
      });
      navigate("/login", { replace: true });
      return;
    }

    setIsResending(true);

    try {
      const response = await resendSuperAdminOtpApi({
        email: userData.email,
      });

      if (response?.status === 200) {
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: "OTP has been resent to your email address.",
          open: true,
        });

        startCountdown();
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response.data?.message || "Failed to resend OTP. Please try again.",
          open: true,
        });
        setResendDisabled(false);
        CCLoader(false);
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "An error occurred while resending OTP. Please try again.",
        open: true,
      });
      CCLoader(false);
      setResendDisabled(false);
    } finally {
      setIsResending(false);
      CCLoader(false);
    }
  };

  const handleRequestTotpReset = async () => {
    if (!userData.email) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "Session expired. Please login again.",
        open: true,
      });
      navigate("/login", { replace: true });
      return;
    }

    try {
      CCLoader(true);
      const response = await requestTotpResetApi({ email: userData.email });

      if (response?.status === 200) {
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: "TOTP reset link have been sent to your email address.",
          open: true,
        });
        // Redirect to login or stay on page
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 1000);
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response?.data?.message || "Failed to request TOTP reset.",
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "An error occurred. Please try again.",
        open: true,
      });
    } finally {
      CCLoader(false);
    }
  };

  const closeSuccessSB = () => setNotification((prev) => ({ ...prev, open: false }));

  const formik = useFormik({
    initialValues: { otp: "" },
    validationSchema: Yup.object({
      otp: Yup.string()
        .min(6, VALIDATION_MESSAGES.PROFILE.OTP.INVALID)
        .max(6, VALIDATION_MESSAGES.PROFILE.OTP.INVALID)
        .required(VALIDATION_MESSAGES.PROFILE.OTP.REQUIRED)
        .matches(/^\d{6}$/, "OTP must be exactly 6 digits"),
    }),
    onSubmit: handleSubmit,
  });

  const handleChange = (newValue) => {
    formik.setFieldValue("otp", newValue);
    setOtp(newValue);
  };

  // Redirect to login if no user data
  if (!userData.email) {
    navigate("/login", { replace: true });
    return null;
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <AuthLayout
        title={
          mfaMethod === "totp" ? "SuperAdmin TOTP Verification" : "SuperAdmin OTP Verification"
        }
        description={
          mfaMethod === "totp"
            ? `Enter the 6-digit code from your authenticator app for ${userData.email}`
            : `Enter the 6-digit OTP sent to ${userData.email}`
        }
        illustration={ccLogo}
      >
        <MDBox>
          <MDBox mb={2}>
            <CustomOTPInput
              value={otp}
              onChange={handleChange}
              numInputs={6}
              size="large"
              variant="default"
              error={formik.touched.otp && formik.errors.otp ? formik.errors.otp : null}
            />
          </MDBox>

          <MDBox mt={4} mb={1}>
            <MDButton
              type="submit"
              variant="gradient"
              color="primary"
              size="large"
              fullWidth
              disabled={isVerifying || formik.values.otp.length !== 6}
            >
              {isVerifying ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Verifying...
                </>
              ) : (
                "Verify OTP"
              )}
            </MDButton>
          </MDBox>

          {mfaMethod === "email" && (
            <MDBox mt={2} textAlign="center">
              <MDTypography variant="button" color="text" sx={{ mb: 1 }}>
                Didn't receive the OTP?
              </MDTypography>
              <MDButton
                variant="text"
                color="primary"
                onClick={handleResendOtp}
                disabled={resendDisabled || isResending}
                sx={{ textTransform: "none" }}
              >
                {isResending ? (
                  <>
                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                    Resending...
                  </>
                ) : resendDisabled ? (
                  `Resend OTP (${countdown}s)`
                ) : (
                  "Resend OTP"
                )}
              </MDButton>
            </MDBox>
          )}
          {mfaMethod === "totp" && (
            <MDBox mt={2} textAlign="center">
              <MDTypography variant="button" color="text" sx={{ display: "inline-block" }}>
                Receiving issue in TOTP?
              </MDTypography>
              {/* <MDButton
                variant="text"
                color="primary"
                onClick={() => setConfirmSwitchOpen(true)}
                sx={{ textTransform: "none", display: "inline-block", px: 1 }}
              >
                Use Email Instead
              </MDButton> */}
              {" "}
              <MDTypography
                variant="caption"
                color="primary"
                fontWeight="medium"
                component="span"
                sx={{ cursor: "pointer" }}
                onClick={handleRequestTotpReset}
              >
                Reset TOTP via Email
              </MDTypography>
            </MDBox>
          )}
          <MDBox mt={3} textAlign="center">
            <MDTypography variant="button" color="text">
              <MDTypography
                variant="button"
                color="primary"
                fontWeight="medium"
                component="button"
                type="button"
                onClick={() => {
                  navigate("/login", { replace: true });
                }}
                sx={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                <KeyboardArrowLeft sx={{ width: "18px" }} pt={1} />
                Back to Login
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </AuthLayout>
      <DraggableDialog
        open={confirmSwitchOpen}
        onClose={() => setConfirmSwitchOpen(false)}
        maxWidth="xs"
        dialogTitle="Switch to Email OTP?"
        dialogContent={
          <Typography variant="body2">
            You are about to switch your 2FA method to email OTP. You will need to re-register your
            authenticator app (TOTP) later if you want to use it again.
          </Typography>
        }
        dialogActions={
          <>
            <Button onClick={() => setConfirmSwitchOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleConfirmSwitchToEmail} variant="contained" color="primary">
              Confirm
            </Button>
          </>
        }
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime=""
        open={notification.open}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
    </form>
  );
}

export default SuperAdminOtpVerification;

SuperAdminOtpVerification.propTypes = {};
